import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Building2, ImagePlus, Loader2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

const SOCIAL_FIELDS: { key: string; label: string; placeholder: string }[] = [
  { key: 'social_facebook', label: 'Facebook URL', placeholder: 'https://facebook.com/youragency' },
  { key: 'social_instagram', label: 'Instagram URL', placeholder: 'https://instagram.com/youragency' },
  { key: 'social_linkedin', label: 'LinkedIn URL', placeholder: 'https://linkedin.com/company/youragency' },
  { key: 'social_telegram', label: 'Telegram URL', placeholder: 'https://t.me/youragency' },
  { key: 'social_tiktok', label: 'TikTok URL', placeholder: 'https://tiktok.com/@youragency' },
  { key: 'social_x', label: 'X (Twitter) URL', placeholder: 'https://x.com/youragency' },
];

export function AgencyBrandingForm() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [form, setForm] = useState<any>({});
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data: agency, isLoading } = useQuery({
    queryKey: ['my-agency-branding', user?.id],
    queryFn: async () => {
      const { data } = await supabase.from('agencies').select('*').eq('created_by', user!.id).maybeSingle();
      return data;
    },
    enabled: !!user,
  });

  useEffect(() => {
    if (agency) {
      setForm({
        name: agency.name || '',
        phone: agency.phone || '',
        whatsapp: (agency as any).whatsapp || '',
        email: agency.email || '',
        website: agency.website || '',
        address: agency.address || '',
        city: agency.city || '',
        manager_name: (agency as any).manager_name || '',
        established_year: (agency as any).established_year || '',
        employee_count: (agency as any).employee_count || '',
        description: typeof agency.description === 'object'
          ? (agency.description as any)?.en || ''
          : (agency.description || ''),
        logo_url: agency.logo_url || '',
        social_facebook: (agency as any).social_facebook || '',
        social_instagram: (agency as any).social_instagram || '',
        social_linkedin: (agency as any).social_linkedin || '',
        social_telegram: (agency as any).social_telegram || '',
        social_tiktok: (agency as any).social_tiktok || '',
        social_x: (agency as any).social_x || '',
      });
    }
  }, [agency]);

  if (!user) return null;
  if (isLoading) return <Skeleton className="h-64 rounded-2xl" />;

  if (!agency) {
    return (
      <Card className="rounded-2xl">
        <CardContent className="p-8 text-center">
          <ShieldAlert className="h-10 w-10 mx-auto text-muted-foreground/50 mb-2" />
          <p className="text-sm text-muted-foreground">Only the agency owner can edit branding.</p>
        </CardContent>
      </Card>
    );
  }

  const set = (k: string, v: any) => setForm((p: any) => ({ ...p, [k]: v }));

  const handleLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `${agency.id}/${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from('agency-logos').upload(path, file, { upsert: true });
      if (error) throw error;
      const { data } = supabase.storage.from('agency-logos').getPublicUrl(path);
      set('logo_url', data.publicUrl);
      toast.success('Logo uploaded');
    } catch (err: any) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const desc = typeof agency.description === 'object'
        ? { ...(agency.description as any), en: form.description }
        : form.description;
      const { error } = await supabase.from('agencies').update({
        name: form.name,
        phone: form.phone || null,
        whatsapp: form.whatsapp || null,
        email: form.email || null,
        website: form.website || null,
        address: form.address || null,
        city: form.city || null,
        manager_name: form.manager_name || null,
        established_year: form.established_year ? parseInt(form.established_year) : null,
        employee_count: form.employee_count ? parseInt(form.employee_count) : null,
        description: desc,
        logo_url: form.logo_url || null,
        social_facebook: form.social_facebook || null,
        social_instagram: form.social_instagram || null,
        social_linkedin: form.social_linkedin || null,
        social_telegram: form.social_telegram || null,
        social_tiktok: form.social_tiktok || null,
        social_x: form.social_x || null,
      }).eq('id', agency.id);
      if (error) throw error;
      qc.invalidateQueries({ queryKey: ['my-agency-branding'] });
      qc.invalidateQueries({ queryKey: ['agency'] });
      toast.success('Agency branding saved');
    } catch (err: any) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h2 className="font-display text-xl font-bold flex items-center gap-2">
          <Building2 className="h-5 w-5 text-primary" /> Agency Branding & Contact
        </h2>
        <p className="text-sm text-muted-foreground">Edit how your agency appears publicly on its detail page.</p>
      </div>

      {/* Logo */}
      <Card className="rounded-2xl border-0 shadow-sm">
        <CardContent className="p-5 flex items-center gap-5">
          <div className="h-24 w-24 rounded-md border bg-muted/30 flex items-center justify-center overflow-hidden shrink-0">
            {form.logo_url ? (
              <img src={form.logo_url} alt="logo" className="h-full w-full object-contain" />
            ) : (
              <Building2 className="h-8 w-8 text-muted-foreground" />
            )}
          </div>
          <div className="flex-1">
            <Label className="text-sm font-semibold">Agency Logo</Label>
            <p className="text-xs text-muted-foreground mb-2">Square image recommended. PNG or JPG.</p>
            <label className="inline-flex items-center gap-2 cursor-pointer text-sm font-medium text-primary">
              <input type="file" accept="image/*" className="hidden" onChange={handleLogo} disabled={uploading} />
              {uploading ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</> : <><ImagePlus className="h-4 w-4" /> Upload new logo</>}
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Basic info */}
      <Card className="rounded-2xl border-0 shadow-sm">
        <CardContent className="p-5 grid sm:grid-cols-2 gap-4">
          <Field label="Agency Name"><Input value={form.name} onChange={e => set('name', e.target.value)} /></Field>
          <Field label="Manager Name"><Input value={form.manager_name} onChange={e => set('manager_name', e.target.value)} /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+251 ..." /></Field>
          <Field label="WhatsApp"><Input value={form.whatsapp} onChange={e => set('whatsapp', e.target.value)} placeholder="+251 ..." /></Field>
          <Field label="Email"><Input type="email" value={form.email} onChange={e => set('email', e.target.value)} /></Field>
          <Field label="Website"><Input value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://..." /></Field>
          <Field label="City"><Input value={form.city} onChange={e => set('city', e.target.value)} /></Field>
          <Field label="Address"><Input value={form.address} onChange={e => set('address', e.target.value)} /></Field>
          <Field label="Established Year"><Input type="number" value={form.established_year} onChange={e => set('established_year', e.target.value)} /></Field>
          <Field label="Employee Count"><Input type="number" value={form.employee_count} onChange={e => set('employee_count', e.target.value)} /></Field>
          <div className="sm:col-span-2">
            <Field label="Description (English)">
              <Textarea rows={5} value={form.description} onChange={e => set('description', e.target.value)} placeholder="About your agency..." />
            </Field>
          </div>
        </CardContent>
      </Card>

      {/* Social */}
      <Card className="rounded-2xl border-0 shadow-sm">
        <CardContent className="p-5 space-y-4">
          <div>
            <h3 className="font-semibold text-sm">Social Media — Build Your Brand</h3>
            <p className="text-xs text-muted-foreground">Add the links you want clients to follow.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {SOCIAL_FIELDS.map(s => (
              <Field key={s.key} label={s.label}>
                <Input value={form[s.key] || ''} onChange={e => set(s.key, e.target.value)} placeholder={s.placeholder} />
              </Field>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="gap-2">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save Changes
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs font-medium">{label}</Label>
      {children}
    </div>
  );
}
